import React, { useState, useEffect } from "react";
import axios from "axios";
import ResultReport from "./ResultsReport";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

const MainReport = () => {
  const [showResult, setShowResult] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTest, setSelectedTest] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classTeacherFor, setClassTeacherFor] = useState([]);
  const [subjectTeacherFor, setSubjectTeacherFor] = useState([]);

  // Load Classes & Sections
  useEffect(() => {
    const fetchClassAndSection = async () => {
      const teacherPhone = localStorage.getItem("userPhone");
      if (!teacherPhone) return;

      const API_URL = "https://aadyaacademy.org/api";
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/get-class/${teacherPhone}`);
        const data = res.data;

        // ✅ Class teacher data
        const classData = Array.isArray(data.class_teacher_for)
          ? data.class_teacher_for
          : [];

        // ✅ Convert subject_teacher_for object to array
        let subjectData = [];
        if (data.subject_teacher_for && typeof data.subject_teacher_for === "object") {
          subjectData = Object.entries(data.subject_teacher_for).map(([key, subjects]) => {
            const [cls, sec] = key.split("-");
            return {
              class: cls,
              section: sec,
              subjects: Array.isArray(subjects) ? subjects : [],
            };
          });
        }

        setClassTeacherFor(classData);
        setSubjectTeacherFor(subjectData);

        const classSet = new Set();
        const sectionSet = new Set();

        classData.forEach(({ class: cls, section: sec }) => {
          if (cls) classSet.add(cls);
          if (sec) sectionSet.add(sec);
        });

        subjectData.forEach(({ class: cls, section: sec }) => {
          if (cls) classSet.add(cls);
          if (sec) sectionSet.add(sec);
        });

        setClasses(Array.from(classSet));
        setSections(Array.from(sectionSet));
      } catch (err) {
        console.error("Error fetching class/section:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchClassAndSection();
  }, []);

  // Fetch Tests list from API
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const res = await axios.get("https://aadyaacademy.org/api/tests");
        if (res.data && Array.isArray(res.data.data)) {
          setTests(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching tests:", err);
      }
    };
    fetchTests();
  }, []);

  const handleViewClick = (cls, sec, subjects = []) => {
    if (!selectedTest) {
      alert("Please select a test before proceeding.");
      return;
    }
    setSelectedClass(cls);
    setSelectedSection(sec);
    setSelectedSubjects(subjects);
    setShowResult(true);
    window.history.pushState({}, "", "");
  };

  const handleBackClick = () => {
    setShowResult(false);
    setSelectedClass("");
    setSelectedSection("");
    setSelectedSubjects([]);
    setSearchTerm("");
    setSelectedTest("");
  };

  // Filtering logic
  const matches = (cls, sec) => {
    const search = searchTerm.toLowerCase();
    return (
      (cls?.toLowerCase().includes(search) || sec?.toLowerCase().includes(search)) &&
      (selectedClass === "" || selectedClass === cls) &&
      (selectedSection === "" || selectedSection === sec)
    );
  };

  const filteredResults = [];

  classTeacherFor.forEach(({ class: cls, section: sec }) => {
    if (matches(cls, sec)) {
      filteredResults.push({
        class: cls,
        section: sec,
        type: "Class Teacher",
        subjects: [],
      });
    }
  });

  subjectTeacherFor.forEach(({ class: cls, section: sec, subjects }) => {
    if (matches(cls, sec)) {
      filteredResults.push({
        class: cls,
        section: sec,
        type: "Subject Teacher",
        subjects: subjects || [],
      });
    }
  });

  if (showResult) {
    return (
      <ResultReport
        selectedClass={selectedClass}
        selectedSection={selectedSection}
        selectedSubjects={selectedSubjects}
        selectedTest={selectedTest}
        onBack={handleBackClick}
        classTeacherFor={classTeacherFor}
        subjectTeacherFor={subjectTeacherFor}
      />
    );
  }

  const inputStyle = {
    height: "50px",
    border: "none",
    width: "130px",
    fontSize: "12px",
    padding: "8px 5px",
    borderRadius: "10px",
    backgroundColor: "rgba(0,0,0,0.086)",
    outline: "none",
  };

  const buttonStyle = {
    backgroundColor: "#e0e0e0",
    border: "none",
    borderRadius: "8px",
    padding: "8px",
    cursor: "pointer",
    fontSize: "12px",
  };

  return (
    <div className="assignment-report" style={{ fontSize: "13px" }}>
      <div className="table-header" style={{ marginBottom: "10px" }}>
        <h3 style={{ fontSize: "16px" }}>Results Report</h3>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <select
            value={selectedTest}
            onChange={(e) => setSelectedTest(e.target.value)}
            style={inputStyle}
          >
            <option value="">Test</option>
            {tests.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p style={{ fontSize: "12px" }}>Loading...</p>
      ) : (
        <table style={{ fontSize: "12px", width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>Class</th>
              <th>Section</th>
              <th>Role</th>
              <th>Subjects</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.length > 0 ? (
              filteredResults.map((item, index) => (
                <tr key={index}>
                  <td>{item.class}</td>
                  <td>{item.section}</td>
                  <td>{item.type}</td>
                  <td>{item.subjects.length ? item.subjects.join(", ") : "-"}</td>
                  <td>
                    <button
                      onClick={() =>
                        handleViewClick(item.class, item.section, item.subjects)
                      }
                      title="View"
                      style={buttonStyle}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", fontSize: "12px" }}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MainReport;
